import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import DateSchema from "_shared/dates/date-schema";
import moment, { Moment, unitOfTime } from "moment";
import { IRedlineableField, JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class AHDate implements IRedlineableField {
  public static get TODAY(): AHDate {
    return new AHDate();
  }
  public static fromMoment(momentValue: Moment): AHDate {
    return new AHDate(momentValue.toISOString());
  }
  public static isMoment(value: unknown): value is Moment {
    return moment.isMoment(value);
  }
  public static fromJSON(json: unknown): AHDate | null | undefined {
    if (isNullOrUndefined(json)) return json;
    if (json === "") return null;
    const params = DateSchema.deserialize(json);
    return new AHDate(params);
  }
  public static readonly fromUnknownJSON = (json: JSONType): AHDate | null | undefined =>
    isValidDateJSON(json, { throw: true }) ? AHDate.fromJSON(json) : undefined;

  public get value(): number {
    return this._momentValue.valueOf();
  }
  public get string(): string {
    return this.toString();
  }
  public get isValid(): boolean {
    return this._momentValue.isValid();
  }
  public get isEmpty(): boolean {
    return !this._momentValue.isValid();
  }
  public get asMoment(): Moment {
    return this._momentValue;
  }
  public get year(): number {
    return this._momentValue.year();
  }
  public get asFormattedString(): string {
    return this._momentValue.format("MM/DD/YYYY");
  }

  public get TimeOfDayGreeting(): string {
    const splitAfternoon = 12; // 24hr time to split the afternoon
    const splitEvening = 17; // 24hr time to split the evening
    const currentHour = Number.parseFloat(this.format("HH"));

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      // Between 12 PM and 5PM
      return "Good afternoon";
    } else if (currentHour >= splitEvening) {
      // Between 5PM and Midnight
      return "Good evening";
    }
    // Between dawn and noon
    return "Good morning";
  }

  public constructor(value?: string | number | (number | string)[]) {
    this._momentValue = moment(value);
  }

  public valueOf(): number {
    return this._momentValue.valueOf();
  }

  public toJSON(): string {
    return this._momentValue.toISOString();
  }

  public toString(format?: string | undefined): string {
    return this._momentValue.format(format ?? "MM/DD/YYYY");
  }

  public isEqualTo(other: AHDate | null | undefined): boolean {
    return this.isSame(other);
  }

  public clone(): AHDate {
    return getDateFromMoment(this._momentValue.clone());
  }

  /* Moment API Wrappers */
  public isSame = (other?: AHDate | null, granularity?: unitOfTime.StartOf): boolean =>
    this._momentValue.isSame(other?._momentValue, granularity ?? "minute");

  public isBefore = (other?: AHDate | null, granularity?: unitOfTime.StartOf): boolean =>
    this._momentValue.isBefore(other?._momentValue, granularity);

  public isAfter = (other?: AHDate | null, granularity?: unitOfTime.StartOf): boolean =>
    this._momentValue.isAfter(other?._momentValue, granularity);

  public add = (amount: number, unit: unitOfTime.DurationConstructor): AHDate =>
    getDateFromMoment(this._momentValue.clone().add(amount, unit));

  public subtract = (amount: number, unit: unitOfTime.DurationConstructor): AHDate =>
    getDateFromMoment(this._momentValue.clone().subtract(amount, unit));

  public diff = (other?: AHDate, unit?: unitOfTime.Diff): number | undefined =>
    isNullOrUndefined(other) ? undefined : this._momentValue.diff(other?._momentValue, unit);

  public toISOString = (): string => this._momentValue.toISOString();

  public format = (format: string): string => this._momentValue.format(format);

  public startOf = (unit: unitOfTime.StartOf): AHDate => getDateFromMoment(this._momentValue.clone().startOf(unit));

  public endOf = (unit: unitOfTime.StartOf): AHDate => getDateFromMoment(this._momentValue.clone().endOf(unit));

  public isSameOrBefore = (other?: AHDate | null, granularity?: unitOfTime.StartOf): boolean =>
    this._momentValue.isSameOrBefore(other?._momentValue, granularity ?? "minute");

  private readonly _momentValue: Moment;
}

export type DateJSON = string | number;

function getDateFromMoment(momentValue: Moment): AHDate {
  return new AHDate(momentValue.toISOString());
}

function isValidDateJSON(json: unknown, options?: { throw: boolean }): json is DateJSON | null | undefined {
  if (isNullOrUndefined(json) || ["string", "number"].includes(typeof json)) return true;
  else if (options?.throw) throw new Error("Cannot create Date from non-string/number value");
  return false;
}
